// src/hooks/useMT5.js

import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { updateMT5Credentials, getMT5Credentials } from "../api";

/**
 * Custom hook to manage MT5 Credentials Form only.
 * 
 * Note: The WebSocket connection and Data Fetching (Account/Trades) 
 * are handled directly by Dashboard.jsx. This hook prevents conflicts
 * by not creating a second WebSocket connection.
 */
export const useMT5 = (showAlert) => {
  const { token } = useAuth(); 

  // --- State ---
  const [mt5ModalOpen, setMt5ModalOpen] = useState(false);
  const [mt5Form, setMt5Form] = useState({ login: "", password: "", server: "" });
  const [savingCreds, setSavingCreds] = useState(false);

  // --- Fetch existing credentials on mount (to pre-fill form) ---
  useEffect(() => {
    const loadCredentials = async () => {
      if (!token) return;
      try {
        const res = await getMT5Credentials();
        if (res?.data) {
          setMt5Form({
            login: res.data.login || "",
            password: "", // Always leave password empty for security
            server: res.data.server || ""
          });
        }
      } catch (err) {
        // Silently fail if credentials don't exist yet
        console.warn("Could not load existing MT5 credentials");
      }
    };
    
    if (mt5ModalOpen) {
      loadCredentials();
    }
  }, [token, mt5ModalOpen]);

  // --- Save MT5 credentials ---
  const handleSaveMt5 = async () => {
    if (!mt5Form.login || !mt5Form.password || !mt5Form.server) {
      showAlert({ title: "Validation Error", message: "Please fill all MT5 fields.", type: "error" });
      return;
    }

    setSavingCreds(true);
    try {
      await updateMT5Credentials(mt5Form);
      
      setMt5ModalOpen(false);
      showAlert({ title: "Success", message: "MT5 Credentials saved. Syncing will begin shortly..." });

      // Notify Dashboard.jsx that credentials changed so it can reset/fetch
      window.dispatchEvent(new Event('storage'));
      
    } catch (err) {
      console.error("Error saving MT5 credentials:", err);
      showAlert({ title: "Error", message: "Failed to save MT5 credentials.", type: "error" });
    } finally {
      setSavingCreds(false);
    }
  };

  return {
    mt5ModalOpen,
    setMt5ModalOpen,
    mt5Form,
    setMt5Form,
    savingCreds,
    handleSaveMt5,
  };
};